import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Plus, LogIn, Bell } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";

export default function Decisions() {
  const navigate = useNavigate();
  const { user, loading, login } = useAuth();
  const { t, locale } = useLang();
  const [decisions, setDecisions] = useState([]);
  const [insights, setInsights] = useState(null);
  const [fetching, setFetching] = useState(true);

  const OUTCOMES = { better: t("detail.outcomeBetter"), expected: t("detail.outcomeExpected"), worse: t("detail.outcomeWorse") };

  useEffect(() => {
    if (!user) { setFetching(false); return; }
    api.get("/decisions").then((r) => setDecisions(r.data)).catch(() => {}).finally(() => setFetching(false));
    api.get("/insights").then((r) => setInsights(r.data)).catch(() => {});
  }, [user]);

  const fmt = (iso) => new Date(iso).toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });

  if (loading) return <AppShell><div className="h-40 animate-pulse rounded-2xl bg-decide-mist" /></AppShell>;

  if (!user) {
    return (
      <AppShell>
        <div className="mt-16 flex flex-col items-center text-center">
          <h1 className="text-2xl font-medium text-decide-ink">{t("decisions.signinTitle")}</h1>
          <p className="mt-2 max-w-xs text-decide-slate">{t("decisions.signinSub")}</p>
          <button data-testid="signin-btn" onClick={login} className="btn-accent mt-6"><LogIn size={18} /> {t("nav.signinGoogle")}</button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-light tracking-tight text-decide-ink">{t("decisions.title")}</h1>
        <button data-testid="new-from-decisions" onClick={() => navigate("/app")} className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-decide-panel text-white" aria-label={t("decisions.makeFirst")}>
          <Plus size={20} />
        </button>
      </div>

      {fetching ? (
        <div className="mt-6 space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-decide-mist" />)}</div>
      ) : decisions.length === 0 ? (
        <div className="mt-20 flex flex-col items-center text-center" data-testid="decisions-empty">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-decide-line bg-decide-surface">
            <Plus size={26} className="text-decide-mute" />
          </div>
          <h2 className="mt-5 text-xl font-medium text-decide-ink">{t("decisions.emptyTitle")}</h2>
          <p className="mt-2 text-decide-slate">{t("decisions.emptySub")}</p>
          <button data-testid="make-first-decision" onClick={() => navigate("/app")} className="btn-accent mt-6">{t("decisions.makeFirst")}</button>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          <InsightsPanel insights={insights} t={t} />
          {decisions.map((d, i) => (
            <motion.button key={d.id} data-testid="decision-card" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.2) }} onClick={() => navigate(`/decisions/${d.id}`)} className="w-full decide-card !p-4 flex items-center justify-between text-left transition-all hover:border-decide-mute">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-decide-ink truncate">{d.title}</p>
                  {d.review_due && <span className="inline-flex items-center gap-1 rounded-full bg-decide-forestLight px-2 py-0.5 text-[10px] font-bold text-decide-forest"><Bell size={10} /> {t("decisions.review")}</span>}
                </div>
                <p className="text-[13px] text-decide-slate truncate mt-0.5">{d.recommendation_label}</p>
                {d.review ? (
                  <p className="mt-1 inline-flex items-center gap-1.5 text-[12px] font-medium text-decide-forest"><span className="h-1.5 w-1.5 rounded-full bg-decide-forest" />{t("history.recorded")} · {OUTCOMES[d.review.outcome] || ""}</p>
                ) : (
                  <p className="mt-1 inline-flex items-center gap-1.5 text-[12px] font-medium text-decide-mute"><span className="h-1.5 w-1.5 rounded-full bg-decide-mute" />{t("history.awaiting")}</p>
                )}
                <p className="text-[12px] text-decide-mute mt-1">{fmt(d.created_at)}{d.confidence ? ` · ${t("decisions.confidenceSuffix", { x: t(`confidence.${d.confidence}`) })}` : ""}</p>
              </div>
              <ChevronRight size={18} className="text-decide-mute shrink-0 ml-2" />
            </motion.button>
          ))}
        </div>
      )}
    </AppShell>
  );
}

function InsightsPanel({ insights, t }) {
  if (!insights) return null;
  const { completed, insights: list } = insights;
  if (completed >= 3 && list.length > 0) {
    return (
      <div className="rounded-3xl bg-decide-panel p-6 text-white" data-testid="insights-panel">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-decide-panelaccent">{t("insights.title")}</p>
        <p className="mt-1 text-[13px] text-white/60">{t("insights.subtitle")}</p>
        <div className="mt-4 space-y-4">
          {list.map((ins) => (
            <div key={ins.id} data-testid="insight-item" className="border-t border-white/10 pt-4 first:border-0 first:pt-0">
              <p className="text-[15.5px] font-medium leading-snug">{ins.text}</p>
              <p className="mt-1.5 text-[11px] font-bold uppercase tracking-wider text-white/40">{t("insights.why")}</p>
              <p className="mt-0.5 text-[13px] leading-relaxed text-white/70">{ins.why}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (completed >= 1) {
    return (
      <div className="rounded-2xl border border-decide-line bg-decide-surface p-5" data-testid="insights-keepgoing">
        <p className="font-semibold text-decide-ink">{t("insights.keepTitle")}</p>
        <p className="mt-1.5 text-[14px] leading-relaxed text-decide-slate">{t("insights.keepBody")}</p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-decide-line bg-decide-surface p-5" data-testid="insights-empty">
      <p className="font-semibold text-decide-ink">{t("insights.emptyTitle")}</p>
      <p className="mt-1.5 text-[14px] leading-relaxed text-decide-slate">{t("insights.emptyBody")}</p>
    </div>
  );
}
